import React, { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { FormikProps } from "formik"
import { FieldArray, FormikProvider, useFormik } from "formik"
import { v4 as uuidv4 } from "uuid"

import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import { Operators } from "../../types/custom/projects"
import type { BaseCondition, CreateRulesetRequest, UpdateRulesetRequest } from "../../types/custom/rules"
import type { RuleList } from "../../types/generated/api/RuleList"
import { RuleSetCreateRequest } from "../../types/generated/api/RuleSetCreateRequest"
import type { RuleSetGroupRetrieve } from "../../types/generated/api/RuleSetGroupRetrieve"
import type { RuleSetUpdate } from "../../types/generated/api/RuleSetUpdate"
import type { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import { ConditionParser, parseConditionIntoString } from "../../utils/conditionHelpers"
import { Condition } from "./components/Condition"

import styles from "./WorkflowBlocksDialog.module.scss"

interface Ruleset {
  name: string
  rules: Rule[]
}

export interface Rule {
  feature: string
  operator: string
  value: string
  type?: string
  secondValue?: string
  returnValue?: string
  label_id?: string
  uuid?: string
  created_at?: string
  id?: string
  andOr?: string
  valueOrFeature?: string
  schemaFeatureType?: WorkflowInputFeature.type
}

type DecisionDialogType = {
  isOpen: boolean
  onClose: () => void
  rulesetName: string | undefined
  rulesetId: string
  readMode: boolean | null
  rules: RuleList[] | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflowFormik: FormikProps<any>
}

/**
 * Dialog for creating, reading and updating ruleset with type decision
 * @param {boolean} isOpen indicator to open dialog or not
 * @param {function} onClose handler for closing dialog
 * @param {string} rulesetName
 * @param {string}  rulesetId
 * @param {boolean} readMode
 * @param {RuleList[]} rules rules array
 * @returns {React.reactELement}
 */
export function DecisionNodeDialog(props: Readonly<DecisionDialogType>): React.ReactElement {
  const { isOpen, onClose, readMode, rules, rulesetName, rulesetId, workflowFormik } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("dialogs")

  const MuiTheme = useMuiTheme()
  const queryClient = useQueryClient()
  const theme = getTheme()

  const [currentRuleset, setCurrentRuleset] = useState<RuleSetUpdate>()

  // state to handle the current mode
  const [mode, setMode] = useState<{ create: boolean; edit: boolean }>({
    create: !rulesetId,
    edit: false,
  })

  // fetch single ruleset
  const { data: rulesetData } = useQuery<AxiosResponse<RuleSetGroupRetrieve>, AxiosError>(
    ["ruleset", rulesetId],
    () => KonanAPI.fetchRuleset(projectId as string, rulesetId),
    {
      enabled: !!projectId && !!rulesetId,
      onSuccess: (res) => {
        // to avoid screen blackouts
        try {
          formik.setFieldValue("ruleset.name", res.data.name)

          const convertedRules = res?.data.active_version?.rules?.map((condition) => {
            const parsedCondition = new ConditionParser(condition?.condition)
            return {
              feature: parsedCondition.getFeature(),
              operator: parsedCondition.getOperator(),
              value: parsedCondition.getValue(),
              secondValue: parsedCondition.getSecondValue(),
              returnValue: condition?.return_label.name ?? "",
              label_id: condition?.return_label.uuid,
              created_at: condition?.created_at,
              id: uuidv4(),
            }
          })

          formik.setFieldValue("ruleset.rules", convertedRules)
        } catch (e) {
          console.warn(e)
        }
      },
    },
  )

  // creating a ruleset with type decision
  const createRulesetMutation = useMutation<AxiosResponse, AxiosError, CreateRulesetRequest>(KonanAPI.createRuleset, {
    mutationKey: "ruleset-create",
    onSuccess: (response) => {
      NotificationUtils.toast(t("DecisionNodeDialog.messages.rulesetCreated"), { severity: "success" })
      onClose()
      setCurrentRuleset(response?.data)
      setMode({ create: false, edit: false })
      queryClient.invalidateQueries(["rulesets", projectId])
    },
    onError: () => {
      NotificationUtils.toast(t("DecisionNodeDialog.messages.createError"), { severity: "error" })
    },
  })

  // updating a ruleset with type decision
  const updateRulesetMutation = useMutation<AxiosResponse, AxiosError, UpdateRulesetRequest>(KonanAPI.updateRuleset, {
    mutationKey: "ruleset-updated",
    onSuccess: (response) => {
      NotificationUtils.toast(t("DecisionNodeDialog.messages.rulesetUpdated"), { severity: "success" })
      onClose()
      setCurrentRuleset(response?.data)
      queryClient.invalidateQueries(["rulesets", projectId])
      queryClient.invalidateQueries(["ruleset", rulesetId])
      queryClient.invalidateQueries("ruleset")
      queryClient.invalidateQueries(["workflows", projectId])
      setMode({ create: false, edit: false })
    },
    onError: () => {
      NotificationUtils.toast(t("DecisionNodeDialog.messages.updateError"), { severity: "error" })
    },
  })

  // Yup validation schema
  const validationSchemaDecisionNode = Yup.object({
    ruleset: Yup.object({
      name: Yup.string()
        .required(t("DecisionNodeDialog.validation.featureRequired"))
        .matches(/^[^\s]+$/u, {
          message: t("DecisionNodeDialog.validation.noWhiteSpaces"),
        }),
      rules: Yup.array().of(
        Yup.object().shape({
          feature: Yup.string().required(t("DecisionNodeDialog.validation.featureRequired")),
          returnValue: Yup.string().required(t("DecisionNodeDialog.validation.returnValueRequired")),
          label_id: Yup.string().required(t("DecisionNodeDialog.validation.labelRequired")),
          operator: Yup.string().required(t("DecisionNodeDialog.validation.operatorRequired")),
          value: Yup.number()
            .typeError(t("DecisionNodeDialog.validation.valueMustBeNumber"))
            .required(t("DecisionNodeDialog.validation.valueRequired")),
          secondValue: Yup.string().when(["operator"], (operator) => {
            return operator === "between"
              ? Yup.number()
                  .typeError(t("DecisionNodeDialog.validation.valueMustBeNumber"))
                  .required(t("DecisionNodeDialog.validation.secondValueRequired"))
              : Yup.number()
          }),
        }),
      ),
    }),
  })

  // formik state that contains all fields states and values
  const formik = useFormik<{ ruleset: Ruleset }>({
    validationSchema: validationSchemaDecisionNode,
    initialValues: {
      ruleset: {
        name: currentRuleset?.name ?? rulesetName ?? "",
        rules: [
          {
            feature: "",
            operator: Operators["<="],
            value: "",
            secondValue: "",
            returnValue: "",
            label_id: "",
            created_at: "",
          },
          {
            id: uuidv4(),
            feature: "",
            operator: Operators.between,
            value: "",
            secondValue: "",
            returnValue: "",
            label_id: "",
            created_at: "",
          },
          {
            feature: "",
            operator: Operators[">="],
            value: "",
            secondValue: "",
            returnValue: "",
            label_id: "",
            created_at: "",
          },
        ],
      },
    },
    initialErrors: {
      ruleset: {
        name: "",
        rules: [{}, {}, {}],
      },
    },
    initialTouched: {
      ruleset: {
        name: false,
        rules: [{}, {}, {}],
      },
    },
    onSubmit: async ({ ruleset }) => {
      const rulesArray = ruleset?.rules?.map((condition, index) => {
        // assign condition type to number on submit
        condition.type = "number"
        return {
          return_label: condition.label_id,
          name: `${ruleset.name}-${index + 1}`,
          uuid: condition?.uuid ?? "",
          created_at: condition?.created_at ?? "",
          condition: `(${parseConditionIntoString(condition as BaseCondition)})`,
        }
      })
      if (mode.edit) {
        await updateRulesetMutation.mutateAsync({
          projectId: projectId as string,
          name: rulesetData?.data.name !== ruleset.name ? ruleset.name : undefined,
          rulesetId: rulesetId ?? currentRuleset?.uuid,
          rules: rulesArray,
          type: RuleSetCreateRequest.type.DECISION,
        })
      } else {
        await createRulesetMutation.mutateAsync({
          projectId: projectId as string,
          name: ruleset.name,
          rules: rulesArray,
          type: RuleSetCreateRequest.type.DECISION,
        })
      }
    },
  })

  const handleAddRule = (): void => {
    const rules = formik.values.ruleset.rules
    const newRule = {
      feature: "",
      operator: Operators.between,
      value: "",
      secondValue: "",
      returnValue: "",
      label_id: "",
      id: uuidv4(),
    }

    // Insert the new rule before the last item
    rules.splice(rules.length - 1, 0, newRule)

    // Update the formik values
    formik.setFieldValue("ruleset.rules", rules)
  }

  // switch edit mode to false if we have exiting rules
  useEffect(() => {
    if (rules?.length) {
      setMode({ create: false, edit: false })
    }
  }, [rules])

  // final indicator for readMode in condition
  const ruleInReadMode = !mode.edit && !mode.create

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={"md"}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      onClose={() => {
        onClose()
        setMode({ edit: false, create: mode.create })
      }}
    >
      <DialogTitle className="dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("DecisionNodeDialog.dialogTitle")}</Typography>
        <IconButton
          onClick={() => {
            onClose()
            setMode({ edit: false, create: mode.create })
          }}
          size="small"
          className={"close-icon-button"}
        >
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Grid container display={"flex"} flexDirection={"row"}>
          <Grid className={styles.decisionNodeDialog} display={"flex"} flexGrow={1}>
            <Grid
              className={styles.DecisionDialogSideBar}
              item
              container
              mr={2}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              flexGrow={1}
            >
              <RuleOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />
            </Grid>
            <Grid container item direction={"column"}>
              <Grid item container style={{ height: "fit-content" }} justifyContent={"space-between"}>
                <Grid item xs={4} container direction={"column"}>
                  <Grid item>
                    <Typography variant="label-bold" variantColor={2}>
                      {t("DecisionNodeDialog.labels.decision")}
                    </Typography>
                  </Grid>
                  <Grid item mt={0} container>
                    {ruleInReadMode ? (
                      <Typography variant="h3-bold">{formik.values.ruleset.name ?? rulesetName}</Typography>
                    ) : (
                      <InputText
                        type="text"
                        handleChange={formik.handleChange}
                        value={formik.values.ruleset.name}
                        disabled={formik.isSubmitting}
                        id={`ruleset.name`}
                        widthCalculator
                        placeholder={t("DecisionNodeDialog.labels.decisionName")}
                        hideDescription
                        error={
                          formik.touched.ruleset?.name &&
                          Boolean(formik.errors.ruleset?.name) &&
                          formik.errors.ruleset?.name
                        }
                      />
                    )}
                  </Grid>
                </Grid>

                <Grid item mt={1} xs={4} justifyContent="flex-end" container alignSelf={"flex-start"}>
                  {mode.edit && (
                    <Button
                      style={{ marginRight: "8px" }}
                      onClick={() => {
                        setMode({ create: false, edit: false })
                        formik.setErrors({})
                      }}
                      disabled={formik.isSubmitting}
                      variant="secondary"
                    >
                      {t("DecisionNodeDialog.buttons.cancel")}
                    </Button>
                  )}
                  <Button
                    onClick={
                      mode.create || mode.edit ? formik.submitForm : () => setMode({ create: false, edit: true })
                    }
                    variant={mode.create || mode.edit ? "primary" : "secondary"}
                    isLoading={formik.isSubmitting}
                  >
                    {mode.create && !currentRuleset
                      ? t("DecisionNodeDialog.buttons.confirm")
                      : mode?.edit
                        ? t("DecisionNodeDialog.buttons.save")
                        : t("DecisionNodeDialog.buttons.edit")}
                  </Button>
                </Grid>
              </Grid>

              {/* Conditions*/}
              <FormikProvider value={formik}>
                <FieldArray name={"ruleset.rules"}>
                  {({ remove: levelRemove }) => (
                    <Grid container item mt={0.5} spacing={1}>
                      {formik?.values?.ruleset?.rules?.map((item, index: number) => (
                        <Condition
                          key={item?.id}
                          readMode={Boolean(readMode || currentRuleset)}
                          ruleInReadMode={ruleInReadMode}
                          formik={formik}
                          index={index}
                          item={item}
                          disableRule={formik.isSubmitting}
                          handleRemoveRule={() => levelRemove(index)}
                          mode={mode}
                          schema={workflowFormik?.values?.features ?? []}
                          workflowFormik={workflowFormik}
                        />
                      ))}

                      {/* Add Rule Button */}
                      {(mode.create || mode.edit) && (
                        <Button
                          disabled={formik?.values.ruleset?.rules?.length === 8 || formik.isSubmitting}
                          size="regular"
                          onClick={() => handleAddRule()}
                          tooltip={
                            formik?.values?.ruleset?.rules?.length > 7
                              ? t("DecisionNodeDialog.labels.maxRulesReached")
                              : ""
                          }
                          tooltipPlacement="top"
                        >
                          {t("DecisionNodeDialog.labels.addRule")}
                        </Button>
                      )}
                    </Grid>
                  )}
                </FieldArray>
              </FormikProvider>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
